import { reactive } from 'vue';
import { authorizeUrl, decodeJwt, exchangeCode, logoutUrl, pkceChallenge, randomString, refreshTokens, type TokenResponse } from './cognito';
import { getConfig } from './config';

interface StoredTokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  /** Epoch milliseconds. */
  expiresAt: number;
}

export interface CurrentUser {
  email: string;
  username: string;
  groups: string[];
}

const TOKENS_KEY = 'finances.tokens';
const PKCE_KEY = 'finances.pkce';
const EXPIRY_MARGIN_MS = 60_000;

export const authState = reactive<{ user: CurrentUser | null }>({ user: null });

function loadTokens(): StoredTokens | null {
  try {
    const raw = localStorage.getItem(TOKENS_KEY);
    return raw ? (JSON.parse(raw) as StoredTokens) : null;
  } catch {
    return null;
  }
}

function saveTokens(response: TokenResponse, previousRefreshToken?: string): StoredTokens {
  const tokens: StoredTokens = {
    idToken: response.id_token,
    accessToken: response.access_token,
    // Cognito does not return a new refresh token on refresh: keep the old one.
    refreshToken: response.refresh_token ?? previousRefreshToken,
    expiresAt: Date.now() + response.expires_in * 1000,
  };
  localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
  setUser(tokens.idToken);
  return tokens;
}

function setUser(idToken: string): void {
  const claims = decodeJwt<{ email?: string; 'cognito:username'?: string; 'cognito:groups'?: string[] }>(idToken);
  authState.user = {
    email: claims.email ?? '',
    username: claims['cognito:username'] ?? '',
    groups: claims['cognito:groups'] ?? [],
  };
}

/** Redirects to the Cognito hosted UI. Remembers where to come back to. */
export async function login(returnTo = location.pathname + location.search): Promise<never> {
  const state = randomString(16);
  const verifier = randomString(48);
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ state, verifier, returnTo }));
  location.assign(authorizeUrl(getConfig(), state, await pkceChallenge(verifier)));
  return new Promise<never>(() => undefined); // navigation in progress
}

export function logout(): void {
  localStorage.removeItem(TOKENS_KEY);
  authState.user = null;
  location.assign(logoutUrl(getConfig()));
}

async function refresh(tokens: StoredTokens): Promise<StoredTokens | null> {
  if (!tokens.refreshToken) return null;
  try {
    return saveTokens(await refreshTokens(getConfig(), tokens.refreshToken), tokens.refreshToken);
  } catch {
    localStorage.removeItem(TOKENS_KEY);
    return null;
  }
}

/**
 * Called once at startup: completes the OAuth redirect if present, otherwise makes sure we have
 * a usable session. Returns the path to show, or redirects to Cognito and never resolves.
 */
export async function initAuth(): Promise<string | null> {
  const params = new URLSearchParams(location.search);
  const code = params.get('code');
  const error = params.get('error');

  if (error) throw new Error(`Sign-in failed: ${params.get('error_description') ?? error}`);

  if (code) {
    const pending = JSON.parse(sessionStorage.getItem(PKCE_KEY) ?? 'null') as
      | { state: string; verifier: string; returnTo: string }
      | null;
    sessionStorage.removeItem(PKCE_KEY);
    if (!pending || pending.state !== params.get('state')) {
      // Stale or forged callback: start over.
      history.replaceState(null, '', '/');
      return login('/');
    }
    saveTokens(await exchangeCode(getConfig(), code, pending.verifier));
    history.replaceState(null, '', pending.returnTo || '/');
    return pending.returnTo || '/';
  }

  await getIdToken();
  return null;
}

/** A valid ID token, refreshing it when close to expiry; redirects to login when impossible. */
export async function getIdToken(forceRefresh = false): Promise<string> {
  let tokens = loadTokens();
  if (tokens && (forceRefresh || tokens.expiresAt - EXPIRY_MARGIN_MS < Date.now())) {
    tokens = await refresh(tokens);
  }
  if (!tokens) return login();
  if (!authState.user) setUser(tokens.idToken);
  return tokens.idToken;
}
